/**
 * v1.1 (2026-10-21, T942): the ended banner offers Keep Pro (the shared upgrade dialog, back to this page after paying).
 * TrialStatusBanner — SAY THAT THE TRIAL IS RUNNING, AND SAY WHEN IT STOPS.
 *
 * Every signup from 2026-08-29 starts a 5-day trial with Pro features. Before
 * this, a new account was created `free`, and `create-scan` answered 403
 * `scans_pro_only` — so the first thing a new user tried to do failed, with no
 * explanation of what they had or had not been given.
 *
 * Two states, and they are different sentences:
 *
 *   RUNNING  — how many days are left, said plainly, before it matters.
 *   ENDED    — what changed, and what they still have. Never a bare refusal,
 *              and never a feature that quietly stops working. A user whose
 *              trial ended has not done anything wrong and should not have to
 *              deduce that from a 403.
 *
 * Renders NOTHING for a paying account, and nothing while the tier is still
 * loading — a banner that flashes "trial ended" at an enterprise customer for
 * one frame is worse than a banner that arrives a moment late.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Tip } from '@/components/Tip';
import { Clock, Sparkles } from 'lucide-react';
import { isTrial, trialDaysLeft } from '@/lib/userTier';
import { openUpgradeMoment } from '@/lib/upgradeMoment';
import { PLANS } from '@/lib/planPricing';

type TrialState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'running'; daysLeft: number }
  | { kind: 'ended'; endedAt: string };

export function TrialStatusBanner() {
  const [state, setState] = useState<TrialState>({ kind: 'loading' });

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) { if (alive) setState({ kind: 'none' }); return; }

      // The tier and its expiry live on the same row, read together. Reading
      // them from two places is how they come to disagree.
      const { data, error } = await supabase
        .from('user_credits_v2')
        .select('tier, trial_ends_at')
        .eq('user_id', uid)
        .maybeSingle();

      if (!alive) return;
      if (error) {
        // Say nothing rather than guess. A wrong banner about somebody's plan
        // is worse than no banner.
        console.error('[trial] could not read plan state:', error.message);
        setState({ kind: 'none' });
        return;
      }

      if (isTrial(data?.tier)) {
        const daysLeft = trialDaysLeft(data?.trial_ends_at) ?? 0;
        setState({ kind: 'running', daysLeft });
        return;
      }

      // Not on trial. Did they just come off one? Only then is there anything
      // to say — and only until they upgrade.
      if (String(data?.tier ?? '').toLowerCase() === 'free') {
        const { data: expiry, error: expErr } = await supabase
          .from('trial_expiry_log')
          .select('trial_ended_at')
          .eq('user_id', uid)
          .order('demoted_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!alive) return;
        if (expErr) { setState({ kind: 'none' }); return; }
        if (expiry?.trial_ended_at) {
          setState({ kind: 'ended', endedAt: String(expiry.trial_ended_at) });
          return;
        }
      }
      setState({ kind: 'none' });
    })();
    return () => { alive = false; };
  }, []);

  if (state.kind === 'loading' || state.kind === 'none') return null;

  if (state.kind === 'running') {
    const { daysLeft } = state;
    const dayWord = daysLeft === 1 ? 'day' : 'days';
    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[13px]"
        data-foxchat-id="trial-running-banner"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
        <Tip text="Your trial gives you the Pro feature set. Nothing is charged, and it ends on its own.">
          <span className="font-semibold text-foreground cursor-help">
            {daysLeft === 0
              ? 'Your trial ends today'
              : `${daysLeft} ${dayWord} left on your trial`}
          </span>
        </Tip>
        <span className="text-muted-foreground">
          Scans, Dens and the full toolset are open while it runs.
        </span>
        <Tip text="Compare what each plan includes. Opens the pricing page.">
          <Link
            to="/pricing"
            className="ml-auto font-semibold text-amber-400 underline-offset-2 hover:underline"
          >
            See plans
          </Link>
        </Tip>
      </div>
    );
  }

  const ended = new Date(state.endedAt);
  const endedLabel = Number.isFinite(ended.getTime())
    ? ended.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-[13px]"
      data-foxchat-id="trial-ended-banner"
    >
      <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Tip text="Your trial has finished. Your account and everything you created are unchanged.">
        <span className="font-semibold text-foreground cursor-help">
          Your {PLANS.pro.name} trial ended{endedLabel ? ` on ${endedLabel}` : ''}. Everything you made is kept.
        </span>
      </Tip>
      <span className="text-muted-foreground">
        You are on the Free plan now. Scans keep running within the free daily and
        monthly limit, and everything you already ran is still here.
      </span>
      <span className="ml-auto flex items-center gap-3">
        {/* T942: the way back to Pro, at the moment it is needed. Checkout opens in a new tab and this page is where it brings you back. */}
        <Tip text={`Go back to ${PLANS.pro.name}. Payment opens in a new tab, and you come back to this page with everything as you left it.`}>
          <button
            type="button"
            onClick={() => openUpgradeMoment({ limitType: 'plan', attempting: `Keep ${PLANS.pro.name}`, need: { plan: 'pro', reason: `Your trial had the ${PLANS.pro.name} features. ${PLANS.pro.name} keeps them.` } })}
            className="rounded-md bg-amber-500/90 px-2.5 py-1 font-semibold text-black hover:bg-amber-400"
            data-foxchat-id="trial-ended-keep-pro"
          >
            Keep {PLANS.pro.name}
          </button>
        </Tip>
        <Tip text="Run a check on a person or a company. Free on every plan.">
          <Link to="/dashboard/vetters" className="font-semibold text-foreground underline-offset-2 hover:underline">
            Vetters
          </Link>
        </Tip>
        <Tip text="Compare what each plan includes. Opens the pricing page.">
          <Link to="/pricing" className="font-semibold text-amber-400 underline-offset-2 hover:underline">
            See plans
          </Link>
        </Tip>
      </span>
    </div>
  );
}
